/**
 * @fileoverview PrivateRoute Component
 * @description Route wrapper that requires authentication
 * @module @xtream/frontend/router/PrivateRoute
 *
 * Features:
 * - Redirects unauthenticated users to login
 * - Checks role-based access
 * - Preserves intended destination for redirect after login
 *
 * Updated in FASE 14 for proper auth store integration
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { UserRole } from '@xtream/shared-types';
import { useAuthStore } from '@store/auth.store';
import { ROUTES } from './routes';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface PrivateRouteProps {
  /** Child components to render if authorized */
  children: React.ReactNode;
  /** Roles allowed to access this route (empty = all authenticated users) */
  allowedRoles?: UserRole[];
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * PrivateRoute - Protects routes that require authentication
 *
 * @example
 * ```tsx
 * <Route
 *   path="/dashboard"
 *   element={
 *     <PrivateRoute>
 *       <DashboardPage />
 *     </PrivateRoute>
 *   }
 * />
 * ```
 */
export const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, allowedRoles }) => {
  const location = useLocation();

  // Get auth state from Zustand store
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const isLoading = useAuthStore((state) => state.isLoading);

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="loading-auth">
        <div className="loading-auth__spinner" />
        <p>Verificando autenticación...</p>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    // Save intended destination for redirect after login
    return <Navigate to={ROUTES.LOGIN} state={{ from: location.pathname }} replace />;
  }

  // Check role-based access
  if (allowedRoles && allowedRoles.length > 0 && user?.role) {
    if (!allowedRoles.includes(user.role)) {
      // User doesn't have required role - redirect to dashboard
      return <Navigate to={ROUTES.DASHBOARD} replace />;
    }
  }

  // Authorized - render children
  return <>{children}</>;
};

export default PrivateRoute;
